import React, { createContext, useContext, useEffect, useState } from "react";
import en_translations from "./locales/en.json";
import en_US_translations from "./locales/en-US.json";

const locales = Object.freeze({
  /***************************************************
   Language codes must be in the format xx-XX as they
   are used in conjunction with the javascript method
   'toLocaleString()' which expects this
   **************************************************/
  englishGB: {
    languageCode: "en-GB",
    translations: en_translations,
  },
  englishAU: {
    languageCode: "en-AU",
    translations: en_translations,
  },
  englishUS: {
    languageCode: "en-US",
    translations: en_US_translations,
  },
});

const allLocales = Object.values(locales);

const getSupportedLocaleNames = () => {
  return allLocales.map((locale) => {
    return locale.languageCode;
  });
};

const defaultLocale = locales.englishGB;

const findLocale = (languageCode) => {
  const sanitizedLanguageCode =
    languageCode && languageCode.length > 0 ? languageCode.trim() : "";
  if (sanitizedLanguageCode.length === 0) return null;

  return allLocales.find(
    (locale) => locale.languageCode === sanitizedLanguageCode
  );
};

const LocalizationContext = createContext();
export const useLocalization = () => useContext(LocalizationContext);

export const LocalizationContextProvider = ({ i18n, children }) => {
  const [currentLocale, setCurrentLocale] = useState(i18n?.locale);
  const [supportedLocales, setSupportedLocales] = useState(
    getSupportedLocaleNames()
  );

  useEffect(() => {
    initialize();
  }, []); // eslint-disable-line

  const getBrowserLocale = () => {
    return navigator?.languages && navigator.languages.length > 0
      ? navigator.languages[0]
      : navigator.language;
  };

  const initialize = () => {
    i18n.defaultLocale = defaultLocale.languageCode;
    i18n.fallbacks = true;

    const languageCode = getBrowserLocale();
    console.log(`Initializing App Locale: '${languageCode}'`);

    setLocale(languageCode);
  };

  const setLocale = (languageCode) => {
    try {
      const sanitizedLanguageCode =
        languageCode && languageCode.length > 0 ? languageCode.trim() : "";
      console.log(`Setting App locale: '${sanitizedLanguageCode}'`);

      const locale = findLocale(sanitizedLanguageCode) || defaultLocale;
      if (locale.languageCode !== sanitizedLanguageCode)
        console.log(
          `Setting App locale: Falling back to: '${locale.languageCode}'`
        );

      if (i18n.locale !== locale.languageCode) {
        i18n.locale = locale.languageCode;
        setCurrentLocale(locale.languageCode);

        if (i18n.translations && !(locale.languageCode in i18n.translations)) {
          i18n.translations[locale.languageCode] = locale.translations;
        }
      }

      console.log(`Current App Locale: '${i18n.locale}'`);
    } catch (error) {
      console.log("Setting App locale: Failed");
      throw error;
    }
  };

  const getLocalizedDateString = (dateValue) => {
    /* No guaranteed mechanism to test for 'Date' type so check for presence of method */
    return dateValue?.toLocaleString &&
      typeof dateValue.toLocaleString === "function"
      ? dateValue.toLocaleString(currentLocale)
      : null;
  };

  const getLocalizedDateStringFromUtcValue = (utcDateString) => {
    try {
      if (utcDateString && utcDateString.length > 0) {
        return getLocalizedDateString(new Date(utcDateString.trim()));
      }
    } catch (error) {
      //Ignore
    }
    return null;
  };

  return (
    <LocalizationContext.Provider
      value={{
        currentLocale,
        supportedLocales,
        setLocale,
        getLocalizedDateString,
        getLocalizedDateStringFromUtcValue,
      }}
    >
      {children}
    </LocalizationContext.Provider>
  );
};
